import {
  Box,
  FilterChip,
  Flex,
  Icon,
  InlineLink,
  InputLabel,
  MultiSelectMenu,
  OptionType,
  Pill,
  SelectMenu,
  SortDirection,
  TableColumnType,
  Tag,
  Text,
  Tooltip,
} from '@fullstory/components';
import { intl } from '@fullstory/intl';
import {
  BulkAction,
  BulkActionResourceType,
  BulkActionTable,
  makeBulkAction,
  RowAction,
  SearchFilter,
  standardUserRestrictedActionMessage,
} from '@fullstory/library-ui';
import { DQMRow, DQMSortOrder, EventType } from '@fullstory/proto/fs/services/dx-objects';
import { int64 } from '@fullstory/proto/google/protobuf';
import { Timestamp } from '@fullstory/proto/google/protobuf/timestamp';
import { fullstorySelectors } from '@fullstory/redux';
import {
  generateRoutePath,
  SETTINGS_ELEMENTS_DETAIL,
  SETTINGS_EVENTS_DETAIL,
  SETTINGS_PAGES_DETAIL,
  SETTINGS_PROPERTIES_DETAIL,
} from '@fullstory/routes';
import { shortNumber } from '@fullstory/util/fmt';
import { createPath, History } from 'history';
import React, { ComponentProps, CSSProperties, ReactNode, useCallback, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { DQMFilters } from '../../types';
import { directionFromSortDirection, propertyTextAppearanceColor } from '../../utils';
import { ClassificationCellContent } from '../ClassificationCellContent';
import { ObjectDetailsTooltip } from '../ObjectDetailsTooltip';
import { ReferencesTooltip } from '../ReferencesTooltip';
import { SuggestionsEmptyState } from '../SuggestionsEmptyState';
import css from './index.module.scss';

type Row = DQMRow;
interface Stringer {
  toString(): string;
}

const pendingVolumeTooltip = (objectType: BulkActionResourceType) =>
  intl.formatMessage(
    {
      defaultMessage:
        'It takes up to one hour to collect {objectType} volume. Please check back later.',
      id: '2cRCNn',
    },
    { objectType: objectType.toLowerCase() },
  );
const pendingIssuesTooltip = intl.formatMessage({
  defaultMessage: 'This issue will remain pending for 72 hours or until activity returns.',
  id: 'I2O0Wx',
});
const pendingTooltipPillText = intl.formatMessage({ defaultMessage: 'Pending', id: 'eKEL/g' });
const issuesTooltip = intl.formatMessage({
  defaultMessage: '0 traffic in the last 72 hours',
  id: 'LlMgII',
});
const isSuggestion = (row: DQMRow): boolean => row.id === '0' && !!row.suggestionId;
const notApplicableCellValue = <Text appearance="bodySmall">--</Text>;

// getReferencesTab is used to determine the tab to navigate to when clicking on the references count and allows
// us to supply specific logic depending on where the tab is located for the specific object type.
export const getReferencesTab = (supportsProperties: boolean) =>
  `?tab=${supportsProperties ? 3 : 2}`;

export interface ColumnWidths {
  name?: CSSProperties['width'];
  creator?: CSSProperties['width'];
  lastModified?: CSSProperties['width'];
  volume?: CSSProperties['width'];
}

const getColumns = (
  onEdit: (row: Row) => void,
  archivedFilterActive: boolean,
  supportsProperties: (row: Row) => boolean,
  quotaExceeded: boolean | undefined,
  objectType: BulkActionResourceType,
  history: History,
  orgId: string,
  tooltipContent?: (object: DQMRow, onEdit?: () => void) => React.ReactNode,
  referencesColumn?: boolean,
  referencesTooltipContent?: (object: DQMRow) => React.ReactNode,
  showDataDirect?: boolean,
  optionalColumns?: TableColumnType<Row>[],
  getName?: (row: Row) => string,
  noLastModified?: boolean,
  widths?: ColumnWidths,
  optionalLeadingColumns?: TableColumnType<Row>[],
  optionalTrailingColumns?: TableColumnType<Row>[],
  classificationColumn?: boolean,
  hideCreator?: boolean,
  hideIssues?: boolean,
): TableColumnType<Row>[] => {
  const objTypeForTestId = `${objectType.toLowerCase()}s`;

  const columns: TableColumnType<Row>[] = [
    ...(optionalLeadingColumns || []),
    {
      key: DQMSortOrder.By.BY_NAME.toString(),
      label: (
        <Text appearance="bodySmallBold" testId={`${objTypeForTestId}-table-sort-by-name`}>
          <FormattedMessage defaultMessage="Name" id="HAlOn1" />
        </Text>
      ),
      sortable: true,
      width: widths?.name ?? '25%',
      renderCell: object => {
        const objectName = (
          <Flex justifyContent="flex-start" gap={12}>
            <Text
              className={css.dqmTableName}
              appearance="bodySmall"
              color={propertyTextAppearanceColor(object)}
              noWrap
            >
              {getName ? getName(object) : object.name}
            </Text>
            {object.pendingProperty && (
              <Tooltip
                tooltipContent={intl.formatMessage({
                  defaultMessage: 'Waiting to receive data',
                  id: '8gVH3D',
                })}
              >
                <Icon name="information" color="secondary" />
              </Tooltip>
            )}
          </Flex>
        );
        return (
          <>
            {tooltipContent ? (
              <ObjectDetailsTooltip
                key={object.id}
                object={object}
                onEdit={() => onEdit(object)}
                tooltipContent={() => tooltipContent(object, () => onEdit(object))}
              >
                {objectName}
              </ObjectDetailsTooltip>
            ) : (
              objectName
            )}
          </>
        );
      },
    },
  ];

  // Add creator column conditionally
  if (!hideCreator) {
    columns.push({
      key: DQMSortOrder.By.BY_CREATOR.toString(),
      label: (
        <Text appearance="bodySmallBold" testId={`${objTypeForTestId}-table-sort-by-creator`}>
          <FormattedMessage defaultMessage="Creator" id="STGKH9" />
        </Text>
      ),
      sortable: true,
      width: widths?.creator ?? '15%',
      renderCell: object => (
        <Text appearance="bodySmall" color={propertyTextAppearanceColor(object)} noWrap>
          {object.createdBy}
        </Text>
      ),
    });
  }

  // Will hit in all cases but properties.
  if (!noLastModified) {
    columns.push({
      key: DQMSortOrder.By.BY_LAST_MODIFIED.toString(),
      label: (
        <Text appearance="bodySmallBold" testId={`${objTypeForTestId}-table-sort-by-last-modified`}>
          <FormattedMessage defaultMessage="Last modified" id="1Jufsz" />
        </Text>
      ),
      sortable: true,
      initialSortDirection: 'desc',
      width: widths?.lastModified ?? '10%',
      renderCell: object => {
        const text = () => {
          const lastUpdated = Timestamp.toDate(object.lastModified);
          if (!lastUpdated || object.lastModified === Timestamp.epoch || object.pendingProperty) {
            return '--';
          } else {
            return intl.formatMessage(
              { defaultMessage: '{lastUpdated, date, medium}', id: 'JNHjt6' },
              { lastUpdated },
            );
          }
        };
        return (
          <Text appearance="bodySmall" color={propertyTextAppearanceColor(object)}>
            {text()}
          </Text>
        );
      },
    });
  }

  if (optionalColumns) {
    columns.push(...optionalColumns);
  }

  const getCorrectObjectId = (row: Row) =>
    objectType.toLocaleLowerCase() == 'page' ? row.friendlyId : row.id;

  if (classificationColumn) {
    columns.push({
      key: 'classification',
      label: (
        <Text
          appearance="bodySmallBold"
          testId={`${objTypeForTestId}-table-sort-by-classification`}
        >
          <FormattedMessage defaultMessage="Classification" id="tC2sDr" />
        </Text>
      ),
      sortable: false, // Can make sortable later if needed
      width: '10%',
      renderCell: object => <ClassificationCellContent objectId={object.id} />,
    });
  }

  if (referencesColumn) {
    const ReferenceLink = ({ row }: { row: Row }) => (
      <Text appearance="bodySmall">
        <InlineLink
          type="button"
          onClick={e => {
            e.stopPropagation();
            history.push(
              createPath({
                pathname: generateRoutePath(pickRoute(objectType), {
                  orgId,
                  id: getCorrectObjectId(row),
                }),
                search: getReferencesTab(supportsProperties(row)),
              }),
            );
          }}
        >
          {row.objectReferenceCount.toString()}
        </InlineLink>
      </Text>
    );
    columns.push({
      key: DQMSortOrder.By.BY_OBJECT_REFERENCE_COUNT.toString(),
      label: (
        <Text appearance="bodySmallBold" testId={`${objTypeForTestId}-table-sort-by-references`}>
          <FormattedMessage defaultMessage="References" id="nCQY4D" />
        </Text>
      ),
      tooltip: intl.formatMessage({
        defaultMessage: 'Number of references to this object',
        id: '7kNbVo',
      }),
      sortable: true,
      align: 'right',
      renderCell: row => (
        <>
          {isSuggestion(row) || row.pendingProperty ? (
            <Text appearance="bodySmall" color={propertyTextAppearanceColor(row)}>
              --
            </Text>
          ) : referencesTooltipContent ? (
            <ReferencesTooltip
              objectId={getCorrectObjectId(row)}
              tooltipContent={() => referencesTooltipContent(row)}
            >
              <ReferenceLink row={row} />
            </ReferencesTooltip>
          ) : (
            <ReferenceLink row={row} />
          )}
        </>
      ),
    });
  }

  columns.push({
    key: DQMSortOrder.By.BY_VOLUME_COUNT.toString(),
    label: (
      <Text
        appearance="bodySmallBold"
        testId={`${objTypeForTestId}-table-sort-by-30-day-event-volume`}
      >
        {showDataDirect ? (
          <FormattedMessage defaultMessage="7-day volume" id="YF45nM" />
        ) : (
          <FormattedMessage defaultMessage="30-day volume" id="i9G9n5" />
        )}
      </Text>
    ),
    sortable: true,
    initialSortDirection: 'desc',
    align: 'right',
    width: widths?.volume ?? '11%',
    renderCell: row => {
      if (isSuggestion(row) || archivedFilterActive || row.pendingProperty) {
        return (
          <Text appearance="bodySmall" color={propertyTextAppearanceColor(row)}>
            --
          </Text>
        );
      }

      if (row.eventSupport === DQMRow.EventSupport.EVENT_SUPPORT_UNSUPPORTED) {
        return (
          <Tooltip
            className={css.tooltip}
            tooltipContent={intl.formatMessage({
              defaultMessage:
                'No action needed. Data quality monitoring does not support this event type.',
              id: 'laE7Fz',
            })}
          >
            {notApplicableCellValue}
          </Tooltip>
        );
      }

      if (row.volumePending) {
        return (
          <Tooltip className={css.tooltip} tooltipContent={pendingVolumeTooltip(objectType)}>
            <Pill variant="compact">{pendingTooltipPillText}</Pill>
          </Tooltip>
        );
      }

      const volume = shortNumber(
        int64.toNumber(showDataDirect ? row.volumeTimeRangeCount : row.volume30DayCount),
      );
      return <Text appearance="bodySmall">{volume}</Text>;
    },
    tooltip: showDataDirect
      ? intl.formatMessage({
          defaultMessage: 'Number of events captured in the past 7 days.',
          id: 'RIELuX',
        })
      : intl.formatMessage({
          defaultMessage: 'Number of events captured in the past 30 days.',
          id: 'ScpgGz',
        }),
  });

  // Add issues column conditionally
  if (!hideIssues) {
    columns.push({
      key: DQMSortOrder.By.BY_ISSUE_COUNT.toString(),
      label: (
        <Text appearance="bodySmallBold" testId={`${objTypeForTestId}-table-sort-by-issues`}>
          <FormattedMessage defaultMessage="Issues" id="Wj5TbN" />
        </Text>
      ),
      sortable: true,
      initialSortDirection: 'desc',
      align: 'center',
      renderCell: row => {
        if (quotaExceeded || row.eventSupport === DQMRow.EventSupport.EVENT_SUPPORT_UNSUPPORTED) {
          return null;
        }
        if (isSuggestion(row) || archivedFilterActive || row.pendingProperty) {
          return (
            <Text appearance="bodySmall" color={propertyTextAppearanceColor(row)}>
              --
            </Text>
          );
        }
        if (row.issuesPending) {
          return (
            <Tooltip className={css.tooltip} tooltipContent={pendingIssuesTooltip}>
              <Pill variant="compact">{pendingTooltipPillText}</Pill>
            </Tooltip>
          );
        }
        if (row.issueCount === 0) {
          return null;
        }
        return (
          <Tooltip tooltipContent={issuesTooltip} className={css.tooltip}>
            <Icon
              testId={`${objectType.toLowerCase()}-table-row-issue-icon`}
              name="warning"
              color="warning"
            />
          </Tooltip>
        );
      },
      tooltip: intl.formatMessage({
        defaultMessage: 'We raise an issue when no events occur in the last 72 hours.',
        id: '+3Q/50',
      }),
    });
  }

  if (optionalTrailingColumns) {
    columns.push(...optionalTrailingColumns);
  }

  return columns;
};

export interface BulkActionsProps<T extends Stringer> {
  noBulkActions?: boolean;
  additionalBulkActions?: BulkAction<Row>[];
  disableSelectAll?: boolean;
  hideBulkArchive?: boolean;
}

export interface DQMTableProps<T extends Stringer> {
  activeSortColumnKey: string;
  activeSortDirection: SortDirection;
  archivedFilter: T;
  addlStatusFilters?: OptionType<T>[];
  filters: T[];
  rows: Row[];
  onArchivedFilterChange: (filter: T) => void;
  onBulkArchive: (rows: Set<Row>, archive: boolean, allRowsSelected: boolean) => void;
  onQueryChange: (query: string) => void;
  onRowArchive: (row: Row, archive: boolean) => void;
  onRowClick: (row: Row) => void;
  onRowDuplicate?: (row: Row) => void;
  onFilterChange: (filters: T[]) => void;
  onSortChange: (order: DQMSortOrder) => void;
  getCount: () => number;
  pagination?: ReactNode;
  query: string;
  quotaExceeded: boolean | undefined;
  objectType: BulkActionResourceType;
  tooltipContent?: (object: DQMRow, onEdit?: () => void) => React.ReactNode;
  optionalFilters: OptionType<T>[] | undefined;
  canModify: boolean | ((row: DQMRow) => boolean);
  activeObjectCount: number;
  activeObjectLimit: number;
  showLimitInStatusFilter: boolean;
  dqmFilters: DQMFilters<T>;
  searchFilterPlaceholder: string;
  showReferencesColumn?: boolean;
  referencesTooltipContent?: (row: Row) => React.ReactNode;
  optionalColumns?: TableColumnType<Row>[];
  optionalLeadingColumns?: TableColumnType<Row>[];
  optionalTrailingColumns?: TableColumnType<Row>[];
  getName?: (row: Row) => string;
  isLoading?: boolean;
  supportsProperties: (row: Row) => boolean;
  filtersDisabled?: boolean;
  noLastModified?: boolean;
  onRenderPage?: ComponentProps<typeof BulkActionTable>['onRenderPage'];
  columnWidths?: ColumnWidths;
  archivedObjectCount?: number;
  archivedIncludedInLimit?: boolean;
  filtersV2?: boolean;
  bulkActionOptions?: BulkActionsProps<T>;
  classificationColumn?: boolean;
  hideCreator?: boolean;
  hideIssues?: boolean;
  hideRowActions?: boolean;
  shouldShowEmptyState?: boolean;
  suggestionsButton?: ReactNode;
}

const pickRoute = (objectType: BulkActionResourceType) => {
  switch (objectType) {
    case 'Element':
      return SETTINGS_ELEMENTS_DETAIL;
    case 'Event':
      return SETTINGS_EVENTS_DETAIL;
    case 'Page':
      return SETTINGS_PAGES_DETAIL;
    case 'Property':
      return SETTINGS_PROPERTIES_DETAIL;
    default:
      throw new Error(`Unsupported object type: ${objectType}`);
  }
};

const pluralizeObjectType = (objectType: BulkActionResourceType, lowercase?: boolean) => {
  if (objectType === 'Property') {
    return lowercase ? 'properties' : 'Properties';
  }

  return `${lowercase ? objectType.toLowerCase() : objectType}s`;
};

// Table columns

function getFilterOptions<T extends Stringer>(
  archivedFilterActive: boolean,
  optionalFilters: OptionType<T>[] | undefined,
  hasIssueFilter: T,
  objectType: BulkActionResourceType,
  createdByMeFilter?: T,
): OptionType<T>[] {
  const objTypeForTestId = pluralizeObjectType(objectType, true);
  let options: OptionType<T>[] = [];

  if (createdByMeFilter) {
    options.push({
      group: ' ', // Intentionally 1 space to indicate no group.
      value: createdByMeFilter,
      label: intl.formatMessage({ defaultMessage: 'Created by me', id: 'GRiT78' }),
      key: createdByMeFilter.toString(),
      testId: `${objTypeForTestId}-table-multi-filter-portal-created-by-me`,
    });
  }

  if (optionalFilters && optionalFilters.length > 0) {
    options = [...options, ...optionalFilters];
  }

  options.push({
    group: intl.formatMessage({ defaultMessage: 'Issues', id: 'Wj5TbN' }),
    value: hasIssueFilter,
    label: intl.formatMessage({ defaultMessage: 'Has issue', id: '0pb7Ek' }),
    key: hasIssueFilter.toString(),
    disabled: archivedFilterActive,
    testId: `${objTypeForTestId}-table-multi-filter-portal-has-issue`,
  });

  return options;
}

export function DQMTable<T extends Stringer>({
  activeSortColumnKey,
  pagination,
  activeSortDirection,
  archivedFilter,
  filters,
  getCount,
  onArchivedFilterChange,
  onBulkArchive,
  onFilterChange,
  onRowArchive,
  onRowClick,
  onRowDuplicate,
  onSortChange,
  rows,
  query,
  onQueryChange,
  quotaExceeded,
  objectType,
  tooltipContent,
  optionalFilters,
  canModify,
  activeObjectCount,
  activeObjectLimit,
  showLimitInStatusFilter,
  dqmFilters,
  showReferencesColumn,
  referencesTooltipContent,
  optionalColumns,
  optionalLeadingColumns,
  optionalTrailingColumns,
  getName,
  isLoading,
  addlStatusFilters = [],
  supportsProperties,
  filtersDisabled,
  noLastModified,
  onRenderPage,
  searchFilterPlaceholder,
  columnWidths,
  archivedObjectCount,
  archivedIncludedInLimit,
  filtersV2,
  bulkActionOptions,
  classificationColumn,
  hideCreator,
  hideIssues,
  hideRowActions,
  shouldShowEmptyState,
  suggestionsButton,
}: DQMTableProps<T>) {
  const intl = useIntl();
  const showObjectLimit = activeObjectLimit > 0 && showLimitInStatusFilter;
  const history = useHistory();
  const orgId = useSelector(fullstorySelectors.getOrgId);
  const showDataDirect = useSelector(fullstorySelectors.getIsDataDirect);

  const {
    noBulkActions = false,
    additionalBulkActions,
    disableSelectAll = false,
    hideBulkArchive = false,
  } = bulkActionOptions ?? {};

  const archivedFilterOptions: OptionType<T>[] = useMemo(
    () => [
      {
        value: dqmFilters.defaultFilter,
        label: filtersV2
          ? intl.formatMessage(
              { defaultMessage: 'Active ({activeObjectCount})', id: 'htGynK' },
              { activeObjectCount },
            )
          : intl.formatMessage({ defaultMessage: 'Active', id: '3a5wL8' }),
        key: dqmFilters.defaultFilter.toString(),
        description: showObjectLimit
          ? intl.formatMessage(
              {
                defaultMessage: 'Limit: {activeObjectCount} / {activeObjectLimit}',
                id: 'RvWmhj',
                description:
                  'Represented as a fraction, {count}/{limit}, or {count} out of {limit}',
              },
              {
                activeObjectCount,
                activeObjectLimit:
                  activeObjectLimit - (archivedIncludedInLimit ? archivedObjectCount ?? 0 : 0),
              },
            )
          : undefined,
      },
      {
        value: dqmFilters.archivedFilter,
        label:
          filtersV2 && !!archivedObjectCount
            ? intl.formatMessage(
                { defaultMessage: 'Archived ({archivedObjectCount})', id: 'nBYp9f' },
                { archivedObjectCount },
              )
            : intl.formatMessage({ defaultMessage: 'Archived', id: '0HT+Ib' }),
        description:
          showObjectLimit && archivedIncludedInLimit && archivedObjectCount
            ? intl.formatMessage(
                {
                  defaultMessage: 'Count: {archivedObjectCount}',
                  id: 'qw2uzK',
                },
                {
                  archivedObjectCount,
                },
              )
            : undefined,
        key: dqmFilters.archivedFilter.toString(),
      },
      ...addlStatusFilters,
    ],
    [
      dqmFilters.defaultFilter,
      dqmFilters.archivedFilter,
      filtersV2,
      intl,
      activeObjectCount,
      showObjectLimit,
      activeObjectLimit,
      archivedIncludedInLimit,
      archivedObjectCount,
      addlStatusFilters,
    ],
  );

  const isArchived = useMemo(
    () => archivedFilter === dqmFilters.archivedFilter,
    [archivedFilter, dqmFilters.archivedFilter],
  );

  const archiveBulkAction = useMemo(
    () =>
      makeBulkAction<Row>({
        key: 'archiveBulkAction',
        label: isArchived
          ? intl.formatMessage({ defaultMessage: 'Unarchive', id: 'ftDYZo' })
          : intl.formatMessage({ defaultMessage: 'Archive', id: 'hrgo+E' }),
        icon: 'folder',
        action: (rows, allRowsSelected) => {
          const filtered = [...rows].filter(row => !row.pendingProperty);
          const filteredSet = new Set(filtered);
          onBulkArchive(filteredSet, !isArchived, allRowsSelected);
        },
        restricted: !canModify,
        restrictedTooltip: standardUserRestrictedActionMessage,
      }),
    [isArchived, onBulkArchive, canModify, intl],
  );

  const resolvedBulkActions = useMemo(
    () => [...(hideBulkArchive ? [] : [archiveBulkAction]), ...(additionalBulkActions ?? [])],
    [hideBulkArchive, archiveBulkAction, additionalBulkActions],
  );

  const rowActions: RowAction<Row>[] = React.useMemo(() => {
    if (hideRowActions) {
      return [];
    }

    const objTypeForTestId = pluralizeObjectType(objectType, true);
    const actions: RowAction<Row>[] = [
      {
        id: 'viewDetail',
        label: intl.formatMessage({ defaultMessage: 'View detail', id: 'q1Y6gS' }),
        iconName: 'search-plus',
        action: onRowClick,
        disabled: row => row.pendingProperty,
        testId: `${objTypeForTestId}-table-action-menu-view-detail`,
      },
    ];

    if (onRowDuplicate && !isArchived) {
      actions.push({
        id: 'rowDuplicate',
        label: intl.formatMessage({ defaultMessage: 'Duplicate', id: '4fHiNl' }),
        iconName: 'duplicate',
        action: onRowDuplicate,
        disabled: row =>
          !canModify ||
          row.eventType === EventType.EVENT_TYPE_CUSTOM_API_EVENT ||
          row.pendingProperty || // API created objects cannot be duplicated
          isSuggestion(row), // Suggestions cannot be duplicated
        testId: `${objTypeForTestId}-table-action-menu-duplicate`,
      });
    }

    actions.push({
      id: 'archiveRow',
      label: isArchived
        ? intl.formatMessage({ defaultMessage: 'Unarchive', id: 'ftDYZo' })
        : intl.formatMessage({ defaultMessage: 'Archive', id: 'hrgo+E' }),
      iconName: 'folder',
      action: object => onRowArchive(object, !isArchived),
      disabled: row => !canModify || row.pendingProperty || isSuggestion(row),
      testId: `${objTypeForTestId}-table-action-menu-archive`,
    });

    return actions;
  }, [
    hideRowActions,
    objectType,
    intl,
    onRowClick,
    onRowDuplicate,
    isArchived,
    canModify,
    onRowArchive,
  ]);

  const selectedArchivedFilterOption = useMemo(
    () => archivedFilterOptions.find(f => f.value === archivedFilter),
    [archivedFilter, archivedFilterOptions],
  );

  const filterOptions = React.useMemo(
    () =>
      getFilterOptions<T>(
        isArchived,
        optionalFilters,
        dqmFilters.hasIssueFilter,
        objectType,
        dqmFilters.createdByMeFilter,
      ),
    [
      dqmFilters.createdByMeFilter,
      dqmFilters.hasIssueFilter,
      isArchived,
      optionalFilters,
      objectType,
    ],
  );

  const selectedFilterOptions = useMemo(
    () => filterOptions.filter(f => filters.includes(f.value)),
    [filterOptions, filters],
  );

  const handleSortChange = (column: string, direction: SortDirection) => {
    const order: DQMSortOrder = {
      by: parseInt(column, 10), // cast enum identifier to int
      direction: directionFromSortDirection(direction),
    };

    onSortChange(order);
  };

  const handleArchivedFilterChange = useCallback(
    (option: OptionType<T> | null) => {
      if (!option) {
        return;
      }

      onArchivedFilterChange(option.value);
    },
    [onArchivedFilterChange],
  );

  const handleFilterChange = useCallback(
    (options: OptionType<T>[]) => {
      onFilterChange(options.map(option => option.value));
    },
    [onFilterChange],
  );

  const columns = React.useMemo(
    () =>
      getColumns(
        onRowClick,
        isArchived,
        supportsProperties,
        quotaExceeded,
        objectType,
        history,
        orgId,
        tooltipContent,
        showReferencesColumn,
        referencesTooltipContent,
        showDataDirect,
        optionalColumns,
        getName,
        noLastModified,
        columnWidths,
        optionalLeadingColumns,
        optionalTrailingColumns,
        classificationColumn,
        hideCreator,
        hideIssues,
      ),
    [
      supportsProperties,
      getName,
      history,
      isArchived,
      objectType,
      onRowClick,
      optionalColumns,
      optionalLeadingColumns,
      optionalTrailingColumns,
      orgId,
      quotaExceeded,
      referencesTooltipContent,
      showDataDirect,
      showReferencesColumn,
      tooltipContent,
      noLastModified,
      columnWidths,
      classificationColumn,
      hideCreator,
      hideIssues,
    ],
  );

  const shouldUseSuggestionId = (rowId: string) => rowId === '0' || rowId === '';

  const renderStatusTabs = () => (
    <Box key="archived">
      <Flex gap={8}>
        {archivedFilterOptions.map(filter => (
          <Tag
            size="md"
            key={filter.key}
            active={selectedArchivedFilterOption?.key === filter.key}
            onClick={() => handleArchivedFilterChange(filter)}
          >
            <Flex gap={8}>
              {filter.leftAdornment}
              {filter.label}
            </Flex>
          </Tag>
        ))}
      </Flex>
    </Box>
  );

  const FiltersV2Content = (resetSelected: () => void) => (
    <Flex column alignItems="flex-start" gap={16}>
      {renderStatusTabs()}
      <Box key="filters" display="flex" gap={8} alignItems="center">
        <InputLabel>
          <FormattedMessage defaultMessage="Filter by" id="S57QRB" />
        </InputLabel>
        <MultiSelectMenu
          testId={`${pluralizeObjectType(objectType, true)}-table-multi-filter`}
          selected={selectedFilterOptions}
          options={filterOptions}
          onChange={opt => {
            handleFilterChange(opt);
            resetSelected();
          }}
          variant="fill-builder"
          placeholder={intl.formatMessage({
            defaultMessage: 'Select',
            id: 'kQAf2d',
          })}
          hoistSelected={false}
          compact
          disabled={filtersDisabled}
        />
      </Box>
      <Flex justifyContent="space-between" gap={8} flexWrap="wrap">
        {selectedFilterOptions.map(filterOption => (
          <FilterChip
            key={filterOption.label}
            label={filterOption.group}
            onRemove={() => {
              onFilterChange(filters.filter(x => x !== filterOption.value));
              resetSelected();
            }}
            testId={`filter-chip-${filterOption.label}`}
          >
            {filterOption.label}
          </FilterChip>
        ))}
      </Flex>
    </Flex>
  );

  return shouldShowEmptyState ? (
    <SuggestionsEmptyState
      generateSuggestionsBtn={suggestionsButton}
      filters={filtersV2 ? renderStatusTabs : undefined}
      objectType={objectType}
    />
  ) : (
    <BulkActionTable
      data-component={`${pluralizeObjectType(objectType, true)}Table`}
      rowKeyAccessor={row => (shouldUseSuggestionId(row.id) ? row.suggestionId : row.id)}
      resourceType={objectType}
      columns={columns}
      fixed
      simplified
      onRenderPage={onRenderPage}
      filters={
        filtersV2
          ? FiltersV2Content
          : resetSelected => (
              <Flex column alignItems="flex-start" gap={8}>
                <Flex justifyContent="flex-start" alignItems="flex-end">
                  <Box key="archived" marginRight={8}>
                    <InputLabel>
                      <FormattedMessage defaultMessage="Status" id="tzMNF3" />
                    </InputLabel>
                    <SelectMenu
                      testId={`${pluralizeObjectType(objectType, true)}-table-status-filter`}
                      selected={selectedArchivedFilterOption}
                      options={archivedFilterOptions}
                      onChange={opt => {
                        handleArchivedFilterChange(opt);
                        resetSelected();
                      }}
                      variant="fill-builder"
                    />
                  </Box>
                  <Box key="filters" marginRight={8}>
                    <InputLabel>
                      <FormattedMessage defaultMessage="Filter by" id="S57QRB" />
                    </InputLabel>
                    <MultiSelectMenu
                      testId={`${pluralizeObjectType(objectType, true)}-table-multi-filter`}
                      selected={selectedFilterOptions}
                      options={filterOptions}
                      onChange={opt => {
                        handleFilterChange(opt);
                        resetSelected();
                      }}
                      variant="fill-builder"
                      placeholder={intl.formatMessage({
                        defaultMessage: 'Select filter',
                        id: 'Xjh3O3',
                      })}
                      hoistSelected={false}
                      compact
                      disabled={filtersDisabled}
                    />
                  </Box>
                  <Box key="search" marginRight={8} flexGrow={1}>
                    <SearchFilter
                      testId={`${pluralizeObjectType(objectType, true)}-table-search-filter`}
                      filter={query}
                      placeholder={searchFilterPlaceholder}
                      setFilter={query => {
                        onQueryChange(query);
                        resetSelected();
                      }}
                    />
                  </Box>
                </Flex>
                <Flex justifyContent="flex-start" gap={8} flexWrap="wrap">
                  {selectedFilterOptions.map(filterOption => (
                    <FilterChip
                      key={filterOption.label}
                      label={filterOption.group}
                      onRemove={() => {
                        onFilterChange(filters.filter(x => x !== filterOption.value));
                        resetSelected();
                      }}
                      testId={`filter-chip-${filterOption.label}`}
                    >
                      {filterOption.label}
                    </FilterChip>
                  ))}
                </Flex>
              </Flex>
            )
      }
      additionalHeaderContent={
        filtersV2 ? (
          <Flex justifyContent="space-between" gap={8} flexWrap="wrap">
            <Box key="search" marginRight={8} flexGrow={1}>
              <SearchFilter
                testId={`${pluralizeObjectType(objectType, true)}-table-search-filter`}
                filter={query}
                placeholder={searchFilterPlaceholder}
                setFilter={query => {
                  onQueryChange(query);
                }}
                size="x-small"
                variant="outline"
                iconSize={16}
              />
            </Box>
            {pagination}
          </Flex>
        ) : (
          pagination
        )
      }
      bulkActions={noBulkActions ? [] : resolvedBulkActions}
      rowActions={rowActions}
      onRowClick={onRowClick}
      rows={rows}
      onSortChange={handleSortChange}
      getCount={getCount}
      testId={`${pluralizeObjectType(objectType, true)}-table`}
      activeSortColumnKey={activeSortColumnKey}
      activeSortDirection={activeSortDirection}
      isPaginated
      isLoading={isLoading}
      isSelectable={row => !row.pendingProperty}
      disableSelectAll={disableSelectAll}
    />
  );
}
